import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, withCtx as _withCtx, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock, resolveComponent as _resolveComponent, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "text-info"
}
const _hoisted_2 = { class: "q-gutter-sm q-mt-md" }

import { useDialogPluginComponent } from "quasar"
import SfxBtn from "src/components/button/SfxBtn.vue"
import SfxInput from "src/components/input/SfxInput.vue"
import SfxTab from "src/components/navigation/SfxTab.vue"
import SfxTabs from "src/components/navigation/SfxTabs.vue"
import SfxTopNavigation from "src/components/navigation/SfxTopNavigation.vue"
import SfxSelect from "src/components/select/SfxSelect.vue"
import SfxSeparator from "src/components/special/SfxSeparator.vue"
import SfxTimeEdit from "src/components/special/SfxTimeEdit.vue"
import { getDB } from "src/db/databases"
import { Log } from "src/util/classes/log"
import { Icons } from "src/util/enums/fontAwesomeIcons"
import { NavigationAction } from "src/util/enums/topNavigation"
import {
  isAttributeChar,
  isAttributeNumeric,
  isAttributeSelect,
  isAttributeSelectOption,
  isAttributeTemporal,
} from "src/util/functions/attributes"
import { unsavedChangesDialog } from "src/util/functions/dialogs"
import {
  fetchAttributeCharListData,
  fetchAttributeNumericListData,
  fetchAttributeSelectListData,
  fetchAttributeSelectOptionsListData,
  fetchAttributeTemporalListData,
  getItemClassListData,
} from "src/util/functions/entityData"
import { decimalStep } from "src/util/functions/misc"
import { showErrorNotification, showSuccessNotification } from "src/util/functions/notifications"
import {
  patchAttributeCharValuesBatch,
  patchAttributeNumericValuesBatch,
  patchAttributeSelectValuesBatch,
  patchAttributeTemporalValuesBatch,
} from "src/util/http/csi/attributes"
import {
  getItemAttributeCharValues,
  getItemAttributeNumericValues,
  getItemAttributeSelectValues,
  getItemAttributeTemporalValues,
  postItem,
} from "src/util/http/csi/items"
import {
  AttributeCharWithValue,
  AttributeNumericWithValue,
  AttributeSelectOptions,
  AttributeSelectWithValue,
  AttributeSelectWithValueFix,
  AttributeTemporalWithValue,
} from "src/util/types/dataModels/attribute"
import { PayloadEntity } from "src/util/types/dataModels/genericObject"
import { Item } from "src/util/types/dataModels/item"
import { ItemClass } from "src/util/types/dataModels/itemClass"
import { ItemModel } from "src/util/types/dataModels/itemModel"
import { computed, onMounted, PropType, ref, shallowRef, watch } from "vue"
import { useI18n } from "vue-i18n"

//region Props and Emits


export default /*#__PURE__*/_defineComponent({
  __name: 'ItemCreateOverlay',
  props: {
  copiedItem: {
    type: Object as PropType<Item>,
  },
},
  setup(__props) {

const props = __props

//endregion

//region Variables

const { dialogRef, onDialogCancel, onDialogOK } = useDialogPluginComponent()
const { t } = useI18n()

const title = computed(() => currentDesignation.value || t("itemCreateOverlay.title"))
const subtitle = computed(() =>
  props.copiedItem ? t("itemCreateOverlay.subtitle_clone") : t("itemCreateOverlay.subtitle_create")
)
const topNavigationContainer = ref<HTMLDivElement | null>(null)

const titleClass = computed(() =>
  !currentDesignation.value ? "sfx-top-navigation__title-placeholder" : undefined
)

const overviewTab = t("itemCreateOverlay.tab_overview")
const locationTab = t("itemCreateOverlay.tab_location")
const dataTab = t("itemCreateOverlay.tab_data")
const attachedTab = t("itemCreateOverlay.tab_attached")

const tabs = [overviewTab, locationTab, dataTab, attachedTab]

const itemIdLabel = t("itemCreateOverlay.label_id")
const designationLabel = t("itemCreateOverlay.label_designation")
const itemClassLabel = t("itemCreateOverlay.label_item_class")

const currentItemId = ref<string | undefined>()
const currentDesignation = ref<string | undefined>()

const currentItemClass = ref<ItemClass | undefined>()
const currentItemModel = ref<ItemModel | undefined>()

const currentItemChar = ref<AttributeCharWithValue[]>([])
const currentItemNumeric = ref<AttributeNumericWithValue[]>([])
const currentItemSelect = ref<AttributeSelectWithValue[]>([])
const currentItemTemporal = ref<AttributeTemporalWithValue[]>([])

const currentItemAttributes = computed(() =>
  [
    ...currentItemChar.value,
    ...currentItemNumeric.value,
    ...currentItemSelect.value,
    ...currentItemTemporal.value,
  ].sort((a, b) => a.name.localeCompare(b.name))
)
const allItemIds = ref<Array<string>>([])
const uniqueItemIdRule = [
  (val: string) => !allItemIds.value.includes(val) || t("itemCreateOverlay.error_value_unique"),
]

const itemClassList = shallowRef<ItemClass[]>([])

const scrollAreaHeight = computed(() => {
  const topNavigationHeight = topNavigationContainer.value?.offsetHeight || 0
  return { height: `calc(100% - ${topNavigationHeight}px)` }
})

//endregion

//region Lifecycle

onMounted(async () => {
  const db = await getDB()

  try {
    await fetchAPIData()
  } catch (fetchError) {
    Log.error({
      message: "There was an error trying to fetch data for item creation",
      error: fetchError,
    })
  }

  allItemIds.value = await db.item.getItemIds()
  const itemClassDocuments = await getItemClassListData()
  itemClassList.value = itemClassDocuments.map((element) => element.toMutableJSON())

  if (props.copiedItem) getCopiedItemItemClass(props.copiedItem)
})

//endregion

//region Functions

async function fetchAPIData() {
  // We don't need item model data currently
  // await fetchItemModelListData()
  await fetchAttributeCharListData()
  await fetchAttributeNumericListData()
  await fetchAttributeSelectListData()
  await fetchAttributeSelectOptionsListData()
  await fetchAttributeTemporalListData()
}

function getCopiedItemItemClass(item: Item) {
  const foundItemClass = itemClassList.value.find((itemClass) => itemClass.id === item.item_class)

  // Should not be theoretically possible since we fetch all item classes beforehand
  if (!foundItemClass) {
    Log.error({ message: "Item class not found", data: { item: JSON.stringify(item) } })
    return
  }

  currentItemClass.value = foundItemClass
}

async function submitData() {
  const item_id = currentItemId.value
  const designation = currentDesignation.value
  const item_class = currentItemClass.value?.id

  // Form should have activated the input requirement check, but let's be sure.
  if (!item_id || !item_class || !designation) {
    return
  }

  const newItem: PayloadEntity<Item> = {
    item_id,
    designation,
    item_class,
    main_image: null,
    attachments: [],
  }

  const createdItem = await createItem(newItem)

  // We failed creating an item, no reason to continue updating its attributes
  if (!createdItem) return

  const mutableItem = createdItem.toMutableJSON()

  // In case this fails, the user has to go to the item data page and edit from there
  await updateItemAttributes(mutableItem)

  // Close the dialog since we have finished submitting the data
  onDialogOK(createdItem)
}

async function createItem(newItem: PayloadEntity<Item>) {
  try {
    const data = await postItem({}, newItem)
    const db = await getDB()
    return await db.item.insert(data)
  } catch (error) {
    if (typeof error === "object" && error !== null) {
      const errorObject = JSON.stringify(error)
      const stringifyPayload = JSON.stringify(newItem)

      showErrorNotification(getItemCreationUserErrors(error))

      Log.error({
        message: "There was an error trying to create an object",
        error: errorObject,
        data: { stringifyPayload },
      })
    }
  }
}

function getItemCreationUserErrors(error: object) {
  let errorForUsers = ""

  if (Object.keys(error).length === 1 && Object.keys(error)[0] === "item_id") {
    errorForUsers = t("itemCreateOverlay.error_item_id")
  } else {
    for (const [index, [key, value]] of Object.entries(error).entries()) {
      if (index !== 0) errorForUsers += "<br>"
      errorForUsers += `${key}: ${value}`
    }
  }
  return errorForUsers
}

async function updateItemAttributes(item: Item) {
  try {
    await updateItemAttributeChar(item)
    await updateItemAttributeNumeric(item)
    await updateItemAttributeSelect(item)
    await updateItemAttributeTemporal(item)
    showSuccessNotification(t("itemCreateOverlay.item_created"))
  } catch {
    showErrorNotification(t("itemCreateOverlay.error_attributes"))
  }
}

async function updateItemAttributeChar(item: Item) {
  const filteredData = currentItemChar.value
    .filter((attribute) => attribute.value != null)
    .map((attribute) => {
      return {
        attribute: attribute.id,
        value: attribute.value,
        item: item.id,
      }
    })

  // No point making the request if there is no data to send
  if (filteredData.length === 0) return

  try {
    const data = await patchAttributeCharValuesBatch({}, filteredData)
    const db = await getDB()
    await db.attribute_char_value.bulkUpsert(data.results)
  } catch (error) {
    handlerAttributePatchError(error, item, filteredData)
  }
}

async function updateItemAttributeNumeric(item: Item) {
  const filteredData = currentItemNumeric.value
    .filter((attribute) => attribute.value != null)
    .map((attribute) => {
      return {
        attribute: attribute.id,
        value: attribute.value,
        item: item.id,
      }
    })

  // No point making the request if there is no data to send
  if (filteredData.length === 0) return

  try {
    const data = await patchAttributeNumericValuesBatch({}, filteredData)
    const db = await getDB()
    await db.attribute_numeric_value.bulkUpsert(data.results)
  } catch (error) {
    handlerAttributePatchError(error, item, filteredData)
  }
}

async function updateItemAttributeSelect(item: Item) {
  const filteredData = currentItemSelect.value
    .filter(
      (attribute): attribute is AttributeSelectWithValueFix =>
        attribute.attribute_select_options != null && attribute.attribute_select_options?.length > 0
    )
    .map((attribute) => {
      const options = Array.isArray(attribute.attribute_select_options)
        ? attribute.attribute_select_options
        : [attribute.attribute_select_options]

      return {
        attribute: attribute.id,
        // Make sure the data is always in an array
        attribute_select_options: options,
        item: item.id,
      }
    })

  // No point making the request if there is no data to send
  if (filteredData.length === 0) return

  try {
    const data = await patchAttributeSelectValuesBatch({}, filteredData)
    const db = await getDB()
    await db.attribute_select_value.bulkUpsert(data.results)
  } catch (error) {
    handlerAttributePatchError(error, item, filteredData)
  }
}

async function updateItemAttributeTemporal(item: Item) {
  const filteredData = currentItemTemporal.value.map((attribute) => {
    return {
      attribute: attribute.id,
      date: attribute.date,
      time: attribute.time,
      item: item.id,
    }
  })

  // No point making the request if there is no data to send
  if (filteredData.length === 0) return

  try {
    const data = await patchAttributeTemporalValuesBatch({}, filteredData)
    const db = await getDB()
    await db.attribute_temporal_value.bulkUpsert(data.results)
  } catch (error) {
    handlerAttributePatchError(error, item, filteredData)
  }
}

function handlerAttributePatchError(error: unknown, item: Item, data: unknown[]) {
  const stringifiedItem = JSON.stringify(item)
  const stringifiedAttributes = JSON.stringify(data)

  Log.error({
    message: "Could not update attributes during item creation",
    error,
    data: {
      item: stringifiedItem,
      filteredAttributes: stringifiedAttributes,
    },
  })

  throw error
}

function resetAttributes() {
  currentItemModel.value = undefined
  currentItemChar.value.length = 0
  currentItemNumeric.value.length = 0
  currentItemSelect.value.length = 0
  currentItemTemporal.value.length = 0
}

function getSelectLabel(itemClass: ItemClass) {
  return itemClass.designation
}

function getSelectValue(itemClass: ItemClass) {
  return itemClass.id
}

function getSelectAttributeLabel(options: AttributeSelectOptions | null) {
  return options?.value
}

function getSelectAttributeValue(options: AttributeSelectOptions | null) {
  return options?.id
}

async function getNewItemAttributeData(newItemClass: ItemClass) {
  const charIDs = newItemClass.attributes_char_item
  const numericIDs = newItemClass.attributes_numeric_item
  const selectIDs = newItemClass.attributes_select_item
  const temporalIDs = newItemClass.attributes_temporal_item

  const db = await getDB()

  const charData = await db.attribute_char.find().where("id").in(charIDs).exec()
  const numericData = await db.attribute_numeric.find().where("id").in(numericIDs).exec()
  const selectData = await db.attribute_select.find().where("id").in(selectIDs).exec()
  const temporalData = await db.attribute_temporal.find().where("id").in(temporalIDs).exec()

  const selectDataWithOptions = await Promise.all(
    selectData.map(async (item) => await item.populateWithOptions())
  )

  currentItemChar.value = charData.map((i) => i.toMutableJSON())
  currentItemNumeric.value = numericData.map((i) => i.toMutableJSON())
  currentItemSelect.value = selectDataWithOptions // Already mutable
  currentItemTemporal.value = temporalData.map((i) => i.toMutableJSON())
}

async function populateCopiedItemValues(itemId: string) {
  await populateCopiedItemChar(itemId)
  await populateCopiedItemNumeric(itemId)
  await populateCopiedItemSelect(itemId)
  await populateCopiedItemTemporal(itemId)
}

async function populateCopiedItemChar(itemId: string) {
  try {
    const db = await getDB()
    const charDataFromAPI = await getItemAttributeCharValues({}, itemId)

    if (charDataFromAPI.results.length > 0) {
      await db.attribute_char_value.bulkUpsert(charDataFromAPI.results)
    }

    const charData = await db.attribute_char_value.find().where("item").eq(itemId).exec()

    for (const attrVal of charData) {
      const foundAttr = currentItemChar.value.find((attr) => attr.id === attrVal.attribute)
      if (foundAttr) foundAttr.value = attrVal.value
    }
  } catch (error) {
    Log.error({
      message: "Error fetching copied item char attribute values",
      error,
      data: { itemId },
    })
  }
}

async function populateCopiedItemNumeric(itemId: string) {
  try {
    const db = await getDB()
    const numericDataFromAPI = await getItemAttributeNumericValues({}, itemId)

    if (numericDataFromAPI.results.length > 0) {
      await db.attribute_numeric_value.bulkUpsert(numericDataFromAPI.results)
    }

    const numericData = await db.attribute_numeric_value.find().where("item").eq(itemId).exec()

    for (const attrVal of numericData) {
      const foundAttr = currentItemNumeric.value.find((attr) => attr.id === attrVal.attribute)
      if (foundAttr) foundAttr.value = attrVal.value
    }
  } catch (error) {
    Log.error({
      message: "Error fetching copied item numeric attribute values",
      error,
      data: { itemId },
    })
  }
}

async function populateCopiedItemSelect(itemId: string) {
  try {
    const db = await getDB()
    const selectDataFromAPI = await getItemAttributeSelectValues({}, itemId)

    if (selectDataFromAPI.results.length > 0) {
      await db.attribute_select_value.bulkUpsert(selectDataFromAPI.results)
    }

    const selectData = await db.attribute_select_value.find().where("item").eq(itemId).exec()

    for (const attrVal of selectData) {
      const foundAttr = currentItemSelect.value.find((attr) => attr.id === attrVal.attribute)
      if (foundAttr) foundAttr.attribute_select_options = attrVal.attribute_select_options
    }
  } catch (error) {
    Log.error({
      message: "Error fetching copied item char temporal values",
      error,
      data: { itemId },
    })
  }
}

async function populateCopiedItemTemporal(itemId: string) {
  try {
    const db = await getDB()
    const temporalDataFromAPI = await getItemAttributeTemporalValues({}, itemId)

    if (temporalDataFromAPI.results.length > 0) {
      await db.attribute_temporal_value.bulkUpsert(temporalDataFromAPI.results)
    }

    const temporalData = await db.attribute_temporal_value.find().where("item").eq(itemId).exec()

    for (const attrVal of temporalData) {
      const foundAttr = currentItemTemporal.value.find((attr) => attr.id === attrVal.attribute)
      if (foundAttr) {
        foundAttr.date = attrVal.date
        foundAttr.time = attrVal.time
      }
    }
  } catch (error) {
    Log.error({
      message: "Error fetching copied item select attribute values",
      error,
      data: { itemId },
    })
  }
}

function selectAttributeValueHandler(selectAttribute: AttributeSelectWithValue) {
  const options = selectAttribute.attribute_select_options?.filter(Boolean)
  return selectAttribute.allow_multiple_selections ? options : options?.[0]
}

function updateSelectAttributeValueHandler(
  options: string[] | string | null,
  selectAttribute: AttributeSelectWithValue
) {
  if (options == null) options = []
  selectAttribute.attribute_select_options = Array.isArray(options) ? options : [options]

  if (isAttributeSelectOption(options)) options = options.id
}

// Check if any of the attributes displayed has any value on it. Returns true on first hit
function existsAttributeWithValue() {
  return (
    currentItemChar.value.some((attribute) => attribute.value) ||
    currentItemNumeric.value.some((attribute) => attribute.value != null) ||
    currentItemSelect.value.some(
      (attribute) =>
        attribute.attribute_select_options && attribute.attribute_select_options.length > 0
    ) ||
    currentItemTemporal.value.some((attribute) => attribute.date || attribute.time)
  )
}

async function updateItemClass(newItemClass: ItemClass) {
  // If any of the attributes has some values, then give a warning
  if (existsAttributeWithValue()) {
    const userWantsToContinue = await unsavedChangesDialog()
    if (!userWantsToContinue) return
  }

  currentItemClass.value = newItemClass
}

//endregion

//region Setup Code

watch(
  () => currentItemClass.value,
  async (newItemClass, previousItemClass) => {
    if (newItemClass) {
      resetAttributes()

      await getNewItemAttributeData(newItemClass)

      // Only in the going to happen in the beginning
      if (!previousItemClass && props.copiedItem) {
        await populateCopiedItemValues(props.copiedItem.id)
      }
    }
  }
)

//endregion

return (_ctx: any,_cache: any) => {
  const _component_q_form = _resolveComponent("q-form")!
  const _component_q_scroll_area = _resolveComponent("q-scroll-area")!
  const _component_q_card = _resolveComponent("q-card")!
  const _component_q_dialog = _resolveComponent("q-dialog")!

  return (_openBlock(), _createBlock(_component_q_dialog, {
    ref_key: "dialogRef",
    ref: dialogRef,
    "transition-show": "slide-up",
    "transition-hide": "slide-down",
    maximized: ""
  }, {
    default: _withCtx(() => [
      _createVNode(_component_q_card, { class: "q-dialog-plugin sfx-item-create-overlay" }, {
        default: _withCtx(() => [
          _createElementVNode("div", {
            ref_key: "topNavigationContainer",
            ref: topNavigationContainer
          }, [
            _createVNode(SfxTopNavigation, {
              title: title.value,
              "title-class": titleClass.value,
              "navigation-action": _unref(NavigationAction).CLOSE,
              "hide-context-bar": "",
              onClose: _unref(onDialogCancel)
            }, {
              subtitle: _withCtx(() => [
                _createElementVNode("span", null, [
                  _createTextVNode(_toDisplayString(subtitle.value) + " ", 1),
                  (props.copiedItem)
                    ? (_openBlock(), _createElementBlock("b", _hoisted_1, _toDisplayString(props.copiedItem.designation), 1))
                    : _createCommentVNode("", true)
                ])
              ]),
              _: 1
            }, 8, ["title", "title-class", "navigation-action", "onClose"])
          ], 512),
          _createVNode(_component_q_scroll_area, {
            class: "full-width scrollbar-area",
            style: _normalizeStyle(scrollAreaHeight.value),
            visible: false
          }, {
            default: _withCtx(() => [
              _createVNode(_component_q_form, {
                onSubmit: submitData,
                class: "q-px-lg"
              }, {
                default: _withCtx(() => [
                  _createVNode(SfxTabs, { "model-value": _unref(dataTab) }, {
                    default: _withCtx(() => [
                      (_openBlock(), _createElementBlock(_Fragment, null, _renderList(tabs, (tab) => {
                        return _createVNode(SfxTab, {
                          key: tab,
                          label: tab,
                          name: tab,
                          disable: tab !== _unref(dataTab),
                          class: "full-width",
                          noCaps: ""
                        }, null, 8, ["label", "name", "disable"])
                      }), 64))
                    ]),
                    _: 1
                  }, 8, ["model-value"]),
                  _createElementVNode("div", _hoisted_2, [
                    _createVNode(SfxInput, {
                      modelValue: currentItemId.value,
                      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((currentItemId).value = $event)),
                      modelModifiers: { trim: true },
                      label: _unref(itemIdLabel),
                      required: "",
                      "hide-bottom-space": "",
                      rules: uniqueItemIdRule
                    }, null, 8, ["modelValue", "label"]),
                    _createVNode(SfxInput, {
                      modelValue: currentDesignation.value,
                      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((currentDesignation).value = $event)),
                      modelModifiers: { trim: true },
                      label: _unref(designationLabel),
                      required: "",
                      "hide-bottom-space": "",
                      maxlength: "256"
                    }, null, 8, ["modelValue", "label"]),
                    _createVNode(SfxSelect, {
                      "model-value": currentItemClass.value,
                      "onUpdate:modelValue": updateItemClass,
                      loading: itemClassList.value.length === 0,
                      label: _unref(itemClassLabel),
                      options: itemClassList.value,
                      "option-label": getSelectLabel,
                      "option-value": getSelectValue,
                      required: "",
                      "hide-bottom-space": ""
                    }, null, 8, ["model-value", "loading", "label", "options"]),
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(currentItemAttributes.value, (item, index) => {
                      return (_openBlock(), _createElementBlock(_Fragment, { key: index }, [
                        (_unref(isAttributeChar)(item))
                          ? (_openBlock(), _createBlock(SfxInput, {
                              key: 0,
                              label: item.name,
                              modelValue: item.value,
                              "onUpdate:modelValue": ($event: any) => ((item.value) = $event),
                              "attr-type": "char",
                              "hide-bottom-space": "",
                              autocomplete: item.autocomplete,
                              allow_blank: item.allow_blank,
                              maxlength: item.max_length,
                              clearable: ""
                            }, null, 8, ["label", "modelValue", "onUpdate:modelValue", "autocomplete", "allow_blank", "maxlength"]))
                          : _createCommentVNode("", true),
                        (_unref(isAttributeNumeric)(item))
                          ? (_openBlock(), _createBlock(SfxInput, {
                              key: 1,
                              label: item.name,
                              modelValue: item.value,
                              "onUpdate:modelValue": ($event: any) => ((item.value) = $event),
                              modelModifiers: { number: true },
                              type: "number",
                              "attr-type": "numeric",
                              "hide-bottom-space": "",
                              minVal: item.min,
                              maxVal: item.max,
                              decimals: item.decimals,
                              step: _unref(decimalStep)(item.decimals),
                              clearable: ""
                            }, null, 8, ["label", "modelValue", "onUpdate:modelValue", "minVal", "maxVal", "decimals", "step"]))
                          : _createCommentVNode("", true),
                        (_unref(isAttributeSelect)(item))
                          ? (_openBlock(), _createBlock(SfxSelect, {
                              key: 2,
                              label: item.name,
                              "model-value": selectAttributeValueHandler(item),
                              "onUpdate:modelValue": ($event: any) => (updateSelectAttributeValueHandler($event, item)),
                              "hide-bottom-space": "",
                              options: item.options,
                              "option-label": getSelectAttributeLabel,
                              "option-value": getSelectAttributeValue,
                              multiple: item.allow_multiple_selections,
                              clearable: "",
                              "emit-value": "",
                              "map-options": ""
                            }, null, 8, ["label", "model-value", "onUpdate:modelValue", "options", "multiple"]))
                          : _createCommentVNode("", true),
                        (_unref(isAttributeTemporal)(item))
                          ? (_openBlock(), _createBlock(SfxTimeEdit, {
                              key: 3,
                              label: item.name,
                              date: item.date,
                              "onUpdate:date": ($event: any) => ((item.date) = $event),
                              time: item.time,
                              "onUpdate:time": ($event: any) => ((item.time) = $event),
                              enable_time: item.enable_time,
                              enable_date: item.enable_date,
                              clearable: ""
                            }, null, 8, ["label", "date", "onUpdate:date", "time", "onUpdate:time", "enable_time", "enable_date"]))
                          : _createCommentVNode("", true)
                      ], 64))
                    }), 128))
                  ]),
                  _createVNode(SfxSeparator, {
                    icon: _unref(Icons).LAYOUTS,
                    dimmed: ""
                  }, null, 8, ["icon"]),
                  _createVNode(SfxBtn, {
                    class: "full-width q-mb-md",
                    type: "submit",
                    label: _ctx.$t('sfxButtons.btn_submit')
                  }, null, 8, ["label"])
                ]),
                _: 1
              })
            ]),
            _: 1
          }, 8, ["style"])
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 512))
}
}

})